import { FC, Fragment, useState, useMemo, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import clsx from 'clsx';
import moment from 'moment';
import { Button, TabContainer, Types } from '@marriott/mi-ui-library';
import { StyledSearchFormDesktop } from './SearchFormDesktop.styles';
import { TabType, SearchFormProps, SearchFormData } from '../SearchForm.types';
import { Destination } from '../Destination';
import { useLocaleStore, useSearchFormStore } from '../../../store';
import { DesktopCalendar } from '../../../molecules/Calendar/DesktopCalendar';
import { CalendarTabValue } from '../../../molecules/Calendar/DesktopCalendar/DesktopCalendar.types';
import { getFormattedDatesObject, validateRoomsAndEventSpace, getPreselectedDateObject } from '../../../utils';
import { ROOMS_TAB_INDEX, EVENT_SPACE_TAB_INDEX, ROOMS_AND_EVENT_SPACE_TAB_INDEX } from '../../../constants';
import { RoomsAndEventSpace } from '../../../molecules/RoomsAndEventSpace';
import {
  RoomsAndEventSpace as RoomsAndEventSpaceType,
  Variant,
} from '../../../molecules/RoomsAndEventSpace/RoomsAndEventSpace.types';

export const SearchFormDesktop: FC<SearchFormProps> = props => {
  const {
    tab,
    destination,
    dates,
    guestRooms,
    attendees,
    guestRoomsAndEventSpace,
    ctas,
    trackingProperties,
    searchResultsFormData,
    isSearchResultPage,
    isSticky,
    onSubmit,
  } = props;

  const { reset, resetAriaLabel } = ctas;

  const roomsAndEventSpaceLabels = {
    guestRooms,
    attendees,
    guestRoomsAndEventSpace,
    reset,
    resetAriaLabel,
  };

  const { loader, setLoader, persistedFormData, setPersistedFormData } = useSearchFormStore();
  const { locale } = useLocaleStore();

  const selectedStartDateValue = isSearchResultPage
    ? persistedFormData?.startDate || searchResultsFormData?.startDate
    : persistedFormData?.startDate;
  const selectedEndDateValue = isSearchResultPage
    ? persistedFormData?.endDate || searchResultsFormData?.endDate
    : persistedFormData?.endDate;
  const selectedStartDate = getPreselectedDateObject(selectedStartDateValue);
  const selectedEndDate = getPreselectedDateObject(selectedEndDateValue);

  const headers = {
    'accept-language': locale,
  };

  const baseTabDetails = [
    { tabName: tab.rooms, tabIcon: 'icon-room-ready' },
    { tabName: tab.eventSpace, tabIcon: 'icon-join-now' },
    { tabName: tab.roomsAndEventSpace, tabIcon: 'icon-meeting-services-app' },
  ];

  const tabDetails = useMemo(() => {
    return isSearchResultPage || isSticky ? baseTabDetails.map(({ tabName }) => ({ tabName })) : baseTabDetails;
  }, [isSearchResultPage, isSticky]);

  const getInitialTabIndex = () => {
    if (!isSearchResultPage || !searchResultsFormData) return ROOMS_TAB_INDEX;
    const rooms = parseInt(persistedFormData?.guestRooms || searchResultsFormData.guestRooms || '0', 10);
    const eventAttendees = parseInt(persistedFormData?.attendees || searchResultsFormData.attendees || '0', 10);
    if (rooms > 0 && eventAttendees > 0) return ROOMS_AND_EVENT_SPACE_TAB_INDEX;
    if (eventAttendees > 0) return EVENT_SPACE_TAB_INDEX;
    return ROOMS_TAB_INDEX;
  };

  const getInitialCalendarTabIndex = () => {
    if (!isSearchResultPage || !searchResultsFormData) return CalendarTabValue.MULTIPLE;
    return selectedStartDate && selectedEndDate && selectedStartDate.isSame(selectedEndDate, 'day')
      ? CalendarTabValue.SINGLE
      : CalendarTabValue.MULTIPLE;
  };

  const [calendarTab, setCalendarTab] = useState<CalendarTabValue>(getInitialCalendarTabIndex);
  const [activeSearchTabIndex, setActiveSearchTabIndex] = useState(getInitialTabIndex);

  const currentTabType =
    activeSearchTabIndex === ROOMS_TAB_INDEX
      ? TabType.ROOMS
      : activeSearchTabIndex === EVENT_SPACE_TAB_INDEX
      ? TabType.EVENTSPACE
      : TabType.ROOMSANDEVENTSPACE;

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    reset: resetForm,
    formState: { errors },
    clearErrors,
  } = useForm<SearchFormData>({
    defaultValues: {
      destination: { description: '', placeId: '' },
      dates: '',
      startDate: '',
      endDate: '',
      guestRooms: '',
      attendees: '',
      roomsAndEventSpace: {
        rooms: 0,
        eventAttendees: 0,
      },
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  useEffect(() => {
    if (!isSearchResultPage || !searchResultsFormData) return;

    const { startDate, endDate, dates } = getFormattedDatesObject(
      searchResultsFormData.startDate,
      searchResultsFormData.endDate
    );
    const updatedFormData = {
      ...persistedFormData,
      destination: persistedFormData.destination?.description
        ? persistedFormData.destination
        : searchResultsFormData.destination,
      startDate: persistedFormData?.startDate || startDate,
      endDate: persistedFormData?.endDate || endDate,
      dates: persistedFormData?.dates || dates,
      guestRooms: persistedFormData?.guestRooms || searchResultsFormData.guestRooms,
      attendees: persistedFormData?.attendees || searchResultsFormData.attendees,
    };

    resetForm({
      ...updatedFormData,
      roomsAndEventSpace: {
        rooms: parseInt(updatedFormData.guestRooms || '0', 10),
        eventAttendees: parseInt(updatedFormData.attendees || '0', 10),
      },
    });
    setPersistedFormData(updatedFormData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitCallback = (data: SearchFormData) => {
    setLoader(true);
    onSubmit(data);
    setPersistedFormData(data);
  };

  const handleKeyEvent = (event: React.KeyboardEvent<HTMLFormElement>) => {
    event.stopPropagation();
  };

  const handleDestinationChange = (destination: {
    description: string;
    placeId?: string;
    secondaryDescription?: string;
  }) => {
    setPersistedFormData({ ...persistedFormData, destination });
    setValue('destination', destination);
    clearErrors('destination');
  };

  const handleCalendarChange = (selectedDates: string, startDate: string, endDate: string) => {
    setPersistedFormData({
      ...persistedFormData,
      dates: selectedDates,
      startDate,
      endDate,
    });

    setValue('startDate', startDate);
    setValue('endDate', endDate);

    if (selectedDates) {
      clearErrors('dates');
    }
  };

  const handleCalendarTabChange = (nextTab: CalendarTabValue) => {
    setCalendarTab(nextTab);

    if (
      nextTab === CalendarTabValue.SINGLE &&
      (activeSearchTabIndex === ROOMS_TAB_INDEX || activeSearchTabIndex === ROOMS_AND_EVENT_SPACE_TAB_INDEX)
    ) {
      setActiveSearchTabIndex(EVENT_SPACE_TAB_INDEX);
    }
  };

  const handleSearchTabChange = (tabIndex: number) => {
    if (
      calendarTab === CalendarTabValue.SINGLE &&
      (tabIndex === ROOMS_TAB_INDEX || tabIndex === ROOMS_AND_EVENT_SPACE_TAB_INDEX)
    ) {
      setCalendarTab(CalendarTabValue.MULTIPLE);
    }

    clearErrors('roomsAndEventSpace');
    setActiveSearchTabIndex(tabIndex);
  };

  const handleRoomsAndEventSpaceChange = (data: RoomsAndEventSpaceType) => {
    const rooms = data.rooms.toString();
    const eventAttendees = data.eventAttendees.toString();
    setValue('guestRooms', rooms);
    setValue('attendees', eventAttendees);
    setPersistedFormData({
      ...persistedFormData,
      guestRooms: rooms,
      attendees: eventAttendees,
    });
    clearErrors('roomsAndEventSpace');
  };

  const isTodaySelected = () => {
    const startDateValue = getValues('startDate');
    const endDateValue = getValues('endDate');

    const startDate = getPreselectedDateObject(startDateValue);
    const endDate = getPreselectedDateObject(endDateValue);
    const today = moment().startOf('day');

    return !!(
      (startDate?.isValid() && startDate.isSame(today, 'day')) ||
      (endDate?.isValid() && endDate.isSame(today, 'day'))
    );
  };

  const destinationField = (
    <Controller
      name="destination"
      control={control}
      rules={{
        validate: value => !!(value && value.description) || destination.requiredError,
      }}
      render={({ field }) => (
        <Destination
          {...field}
          labels={destination}
          customHeaders={headers}
          currentValue={persistedFormData?.destination?.description}
          showErrorMessage={!!errors.destination?.message}
          destinationOnChange={handleDestinationChange}
        />
      )}
    />
  );

  const datesField = (
    <Controller
      name="dates"
      control={control}
      rules={{
        required: dates.requiredError,
        validate: value => {
          if (!value) {
            return true;
          }

          if (calendarTab === CalendarTabValue.MULTIPLE && !getValues('endDate')) {
            return dates.requiredError;
          }

          return !isTodaySelected() || dates?.currentDateError;
        },
      }}
      render={({ field, fieldState }) => (
        <DesktopCalendar
          dates={dates}
          ctas={ctas}
          selectedStartDate={selectedStartDate}
          selectedEndDate={selectedEndDate}
          calendarTab={calendarTab}
          onCalendarTabChange={handleCalendarTabChange}
          isEventSpaceTabActive={activeSearchTabIndex === EVENT_SPACE_TAB_INDEX}
          onChange={(selectedDates, startDate, endDate) => {
            field.onChange(selectedDates);
            handleCalendarChange(selectedDates, startDate, endDate);
          }}
          showErrorMessage={!!fieldState.error?.message}
          errorMessage={fieldState.error?.message}
        />
      )}
    />
  );

  const roomsAndEventSpaceField = (
    <Controller
      name="roomsAndEventSpace"
      control={control}
      rules={{
        validate: value =>
          validateRoomsAndEventSpace(value.rooms.toString(), value.eventAttendees.toString(), currentTabType, {
            rooms: guestRooms.requiredError,
            eventSpace: attendees.requiredError,
            roomsAndEventSpace: guestRoomsAndEventSpace.requiredError,
          }),
      }}
      render={({ field }) => (
        <RoomsAndEventSpace
          {...field}
          labels={roomsAndEventSpaceLabels}
          variant={currentTabType as unknown as Variant}
          value={field.value}
          showErrorMessage={!!errors.roomsAndEventSpace?.message}
          errorMessage={errors.roomsAndEventSpace?.message}
          onChange={data => {
            field.onChange(data);
            handleRoomsAndEventSpaceChange(data);
          }}
        />
      )}
    />
  );

  const submitButton = (
    <Button
      type={Types.ButtonTypeVariation.Submit}
      ariaLabel={isSearchResultPage ? ctas.updateAriaLabel : ctas.findAriaLabel}
      className={clsx('m-button-primary find-cta', loader && 'm-button skeleton-loader')}
      isDisabled={loader}
    >
      <span className="icon-search" /> {isSearchResultPage ? ctas.update : ctas.find}
    </Button>
  );

  const sharedSearchFormDesktop = (
    <div className="row">
      <div className="col-lg-3 px-2">{destinationField}</div>
      <div className="col-lg-3 px-2">{datesField}</div>
      <div className="col-lg-3 px-2">{roomsAndEventSpaceField}</div>
      <div className="col-lg-3 px-2">{submitButton}</div>
    </div>
  );

  return (
    <StyledSearchFormDesktop isSearchResultPage={isSearchResultPage || isSticky}>
      <form onSubmit={handleSubmit(submitCallback)} onKeyUp={handleKeyEvent} onKeyDown={handleKeyEvent}>
        <section>
          <TabContainer
            componentId="search-tabs"
            tabDetails={tabDetails}
            contentBlockAlignment="left"
            flexibleTabWidth={true}
            tabsPerViewInDesktop={tabDetails.length}
            styleclass={clsx('tab-container', isSearchResultPage && 'px-lg-0')}
            layoutContainer=""
            selectedTabIndex={activeSearchTabIndex}
            onTabChange={handleSearchTabChange}
            trackingProperties={{
              ...trackingProperties,
              additionalTrackingVariables: `evar11=${trackingProperties.trackingDescription}`,
            }}
          >
            <Fragment key={ROOMS_TAB_INDEX} />
            <Fragment key={EVENT_SPACE_TAB_INDEX} />
            <Fragment key={ROOMS_AND_EVENT_SPACE_TAB_INDEX} />
          </TabContainer>
        </section>
        <section className={clsx('shared-search-row', isSearchResultPage && 'px-lg-0')}>
          {sharedSearchFormDesktop}
        </section>
      </form>
    </StyledSearchFormDesktop>
  );
};
